import React from 'react';
import {makeStyles} from '@material-ui/core';
import {Colors} from '../../styles/Colors';
import {TFunction} from 'react-i18next';
import {Column} from 'react-table';
import CustomizedIconButton from '../../components/IconButton/IconButton';
import PermissionProvider from "../../components/PermissionProvider/PermissionProvider";
import {AccountPermission} from "../../store/types/Permission";
import { PhonebookRecord } from '../../store/actions/accountPhoneBook/payloads';
import Switch from '../../components/Switch/Switch';
import { YesNo } from '../../store/types/CallScreening';
import PermissionLinkCell from '../../components/DataTable/PermissionLinkCell';
import OverflowTooltip from '../../components/OverflowTooltip/OverflowTooltip';
import {Delete, Edit} from "@material-ui/icons";

export const useStyles = makeStyles(() => ({
    tableContainer: {
        maxWidth: 1040,

        '& thead > tr > th:nth-of-type(1)': {
            justifyContent: 'center',
            justifyItems: 'center',
            textAlign: 'center',
            display: 'flex',
        },
        
        '& thead > tr > th:nth-of-type(4)': {
            justifyContent: 'center',
            justifyItems: 'center',
            textAlign: 'center',
            display: 'flex',
        },
        
        '& thead > tr > th:nth-of-type(6)': {
            justifyContent: 'center',
            justifyItems: 'center',
            textAlign: 'center',
            display: 'flex',
        },

        '& tbody > tr > td:nth-of-type(1)': {
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            paddingRight: '0px !important',
            paddingLeft: '0px !important',
        },
        
        '& tbody > tr > td:nth-of-type(4)': {
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            paddingRight: '0px !important',
            paddingLeft: '0px !important',
        },
        
        '& tbody > tr > td:nth-of-type(6)': {
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            paddingRight: '0px !important',
            paddingLeft: '0px !important',
        },
    },
    inputsContainer: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    button: {
        width: 'fit-content',
        minWidth: 142,
        height: 40,
        marginBottom: 24,

        '& .MuiButton-label': {
            color: Colors.White,
        },
    },
    addIcon: {
        marginRight: 7,
    },
    typeColumn: {
        color: Colors.Gray
    },
    container: {
        '& .MuiPaper-root': {
            width: 336,
        },
        '& .MuiDialogContent-root': {
            minWidth: 'unset !important',
            padding: '24px 24px 26px 24px !important',
            background: `${Colors.White} !important`,
            overflowX: 'hidden'
        },
        '& .MuiDialogActions-root': {
            '& > :first-child': {
                marginRight: 20,
            },
        },
    },
    primaryModalButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    emptySpeedDial: {
        fontWeight: 700,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        color: Colors.Gray5,
        opacity: 0.5,
    },
    overflowCell: {
        minWidth: 0,
    },
}));

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    onSwitchClick: (checked: boolean, record: PhonebookRecord) => void,
    onPhoneNumberClick: (record: PhonebookRecord) => void,
    editRecord: (record: PhonebookRecord) => void,
    deleteRecord: (record: PhonebookRecord) => void,
): Column<PhonebookRecord>[] => {

    return [
        {
            accessor: 'favorite_rating',
            Header: t<string>('screens:accountPhoneBook.favoriteNumber'),
            width: 1,
            Cell: function Cell(params) {
                return (
                    <PermissionProvider permission={AccountPermission.PhoneBook.CompanyContacts.value}>
                        <Switch
                            checked={params.row.original.favorite_rating === YesNo.Yes}
                            onChange={(event) => {
                                onSwitchClick?.(event.target.checked, params.row.original);
                            }}
                            dataQa={'account-phone-book-favorite-switcher' + params.row.original.i_account_phonebook}
                            dataTestId={'account-phone-book-favorite-switcher' + params.row.original.i_account_phonebook}
                            disabled={params.row.original.lock_type != 'None'}
                        />
                    </PermissionProvider>
                );
            },
            minWidth: 160,
        },
        {
            accessor: 'phone_number',
            Header: t<string>('screens:accountPhoneBook.phoneNumber'),
            width: 2,
            Cell: function Cell(params) {
                const permission = AccountPermission.PhoneBook.CompanyContacts.value;
                const isLocked = params.row.original.lock_type == 'Full';
                return isLocked ? (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-phone-book-phone-number-locked"
                            text={params.row.original.phone_number}
                            tooltip={params.row.original.phone_number}
                            copy={false}
                        />
                    </div>
                ) : (
                    <PermissionLinkCell
                        text={params.row.original.phone_number}
                        onClick={() => {
                            onPhoneNumberClick?.(params.row.original);
                        }}
                        permissions={permission}
                    />
                );
            },
            maxWidth: 180,
            minWidth: 180,
        },
        {
            Header: t<string>('common:name'),
            accessor: 'name',
            width: 2,
            Cell: function Cell(params) {
                return (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-phone-book-name"
                            text={params.row.original.name}
                            tooltip={params.row.original.name}
                            copy={false}
                        />
                    </div>
                );
            },
            maxWidth: 180,
            minWidth: 180,
        },
        {
            Header: t<string>('screens:accountPhoneBook.speedDial'),
            accessor: 'dial_id',
            Cell: function Cell(params) {
                if(!params.row.original.dial_id?.length) {
                    return (
                        <div className={classes.emptySpeedDial}>
                            &mdash;
                        </div>);
                }
                return (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-phone-book-dial_id"
                            text={params.row.original.dial_id}
                            tooltip={params.row.original.dial_id}
                            copy={false}
                        />
                    </div>
                );
            },
            maxWidth: 120,
            minWidth: 120,
        },
        {
            Header: t<string>('screens:accountPhoneBook.type'),
            accessor: 'phone_type',
            width: 2,
            Cell: function Cell(params) {
                const text = t(`screens:accountPhoneBook.${params.row.original.phone_type}`);
                return (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-phone-book-phone_type"
                            text={text}
                            tooltip={text}
                            copy={false}
                            classes={{
                                text: classes.typeColumn
                            }}
                        />
                    </div>
                );
            },
            minWidth: 180,
        },
        {
            accessor: 'i_account_phonebook',
            Header: t<string>('common:actions'),
            minWidth: 180,
            width: 1,
            Cell: function Cell(params) {
                const isFullLocked = params.row.original.lock_type == 'Full';
                if(isFullLocked) {
                    return (<></>);
                }
                const isFullPartially = params.row.original.lock_type !== 'None';
                return (<>
                    <CustomizedIconButton
                        onClick={() => {
                            editRecord?.(params.row.original);
                        }}
                        dataTestId="edit-phone-book-list-item-button"
                        dataQa="edit-phone-book-list-item-button"
                        tooltipText={t<string>('common:edit')}
                    >
                        <Edit htmlColor={Colors.ListIconColor}/>
                    </CustomizedIconButton>
                    {!isFullPartially && (
                        <CustomizedIconButton
                            onClick={() => {
                                deleteRecord?.(params.row.original);
                            }}
                            dataTestId="remove-phone-book-list-item-button"
                            dataQa="remove-phone-book-list-item-button"
                            tooltipText={t<string>('common:delete')}
                        >
                            <Delete htmlColor={Colors.ListIconColor}/>
                        </CustomizedIconButton>
                    )}
                </>);
            },
        },
    ];
};
